import { apiClient, getErrorMessage } from '../../utils/apiClient';
import { logger } from '../../utils/logger';
import { toast } from '../../utils/toast';
import {
  API_ADMIN_IATA_CODES_LIST, API_ADMIN_IATA_CODES_CREATE,
  API_ADMIN_IATA_CODES_GET, API_ADMIN_IATA_CODES_UPDATE,
  API_ADMIN_IATA_CODES_DELETE, API_ADMIN_IATA_CODES_ACTIVATE,
  API_ADMIN_IATA_CODES_DEACTIVATE, API_IATA_CODES_ACTIVE,
} from '../../constants/apiConstants';

// ============================================================================
// Types
// ============================================================================

export interface IataCode {
  id: number;
  codeType: string;
  iataCode: string;
  name: string;
  countryCode?: string;
  city?: string;
  icaoCode?: string;
  numericCode?: string;
  airlinePrefix?: string;
  latitude?: number;
  longitude?: number;
  timezone?: string;
  sortOrder: number;
  isActive: boolean;
  status: string;
}

export interface CreateIataCodeRequest {
  codeType: string;
  iataCode: string;
  name: string;
  countryCode?: string;
  city?: string;
  icaoCode?: string;
  numericCode?: string;
  airlinePrefix?: string;
  latitude?: number;
  longitude?: number;
  timezone?: string;
  sortOrder?: number;
}

export interface UpdateIataCodeRequest {
  name: string;
  countryCode?: string;
  city?: string;
  icaoCode?: string;
  numericCode?: string;
  airlinePrefix?: string;
  latitude?: number;
  longitude?: number;
  timezone?: string;
  sortOrder?: number;
}

// ============================================================================
// IATA Code Service
// ============================================================================

export const iataCodeService = {
  /**
   * Fetches paginated IATA codes for admin management.
   */
  async getIataCodes(searchText?: string, status?: string, codeType?: string, pageNumber = 1, pageSize = 20, sortBy?: string, sortDirection?: string) {
    try {
      const response = await apiClient.get(API_ADMIN_IATA_CODES_LIST, {
        params: { searchText, status, codeType, pageNumber, pageSize, sortBy, sortDirection },
      });
      if (!response.data.succeeded) throw new Error(response.data.messages?.join(', '));
      const data = response.data.data;
      return { iataCodes: data?.codes ?? [], totalCount: data?.totalCount ?? 0, pageNumber: data?.pageNumber ?? 1, pageSize: data?.pageSize ?? 20 };
    } catch (error) {
      logger.error('Failed to fetch IATA codes', error);
      throw error;
    }
  },

  /**
   * Fetches active IATA codes for dropdown consumption.
   */
  async getActiveIataCodes(type?: string): Promise<IataCode[]> {
    try {
      const response = await apiClient.get(API_IATA_CODES_ACTIVE, {
        params: type ? { type } : undefined,
      });
      if (!response.data.succeeded) throw new Error(response.data.messages?.join(', '));
      return response.data.data?.codes ?? [];
    } catch (error) {
      logger.error('Failed to fetch active IATA codes', error);
      throw error;
    }
  },

  /**
   * Fetches a single IATA code by ID.
   */
  async getIataCodeById(id: number): Promise<IataCode> {
    try {
      const response = await apiClient.get(API_ADMIN_IATA_CODES_GET(id));
      if (!response.data.succeeded) throw new Error(response.data.messages?.join(', '));
      return response.data.data?.code;
    } catch (error) {
      logger.error('Failed to fetch IATA code', error);
      throw error;
    }
  },

  /**
   * Creates a new IATA code entry.
   */
  async createIataCode(request: CreateIataCodeRequest) {
    try {
      const response = await apiClient.post(API_ADMIN_IATA_CODES_CREATE, request);
      if (!response.data.succeeded) throw new Error(response.data.messages?.join(', '));
      toast.success('IATA code created successfully');
      return response.data.data;
    } catch (error) {
      const msg = getErrorMessage(error);
      toast.error(`Failed to create IATA code: ${msg}`);
      throw error;
    }
  },

  /**
   * Updates an existing IATA code entry.
   */
  async updateIataCode(id: number, request: UpdateIataCodeRequest) {
    try {
      const response = await apiClient.put(API_ADMIN_IATA_CODES_UPDATE(id), request);
      if (!response.data.succeeded) throw new Error(response.data.messages?.join(', '));
      toast.success('IATA code updated successfully');
      return response.data.data;
    } catch (error) {
      const msg = getErrorMessage(error);
      toast.error(`Failed to update IATA code: ${msg}`);
      throw error;
    }
  },

  /**
   * Soft-deletes an IATA code entry.
   */
  async deleteIataCode(id: number) {
    try {
      const response = await apiClient.delete(API_ADMIN_IATA_CODES_DELETE(id));
      if (!response.data.succeeded) throw new Error(response.data.messages?.join(', '));
      toast.success('IATA code deleted successfully');
    } catch (error) {
      const msg = getErrorMessage(error);
      toast.error(`Failed to delete IATA code: ${msg}`);
      throw error;
    }
  },

  /**
   * Activates an IATA code entry.
   */
  async activateIataCode(id: number) {
    try {
      const response = await apiClient.put(API_ADMIN_IATA_CODES_ACTIVATE(id));
      if (!response.data.succeeded) throw new Error(response.data.messages?.join(', '));
      toast.success('IATA code activated');
    } catch (error) {
      const msg = getErrorMessage(error);
      toast.error(`Failed to activate IATA code: ${msg}`);
      throw error;
    }
  },

  /**
   * Deactivates an IATA code entry.
   */
  async deactivateIataCode(id: number) {
    try {
      const response = await apiClient.put(API_ADMIN_IATA_CODES_DEACTIVATE(id));
      if (!response.data.succeeded) throw new Error(response.data.messages?.join(', '));
      toast.success('IATA code deactivated');
    } catch (error) {
      const msg = getErrorMessage(error);
      toast.error(`Failed to deactivate IATA code: ${msg}`);
      throw error;
    }
  },
};
